/**
 * deathDecals.ts — Enemy death floor sprites
 * =====================================================================
 * BETA 16.22 — Per Dylan: "these are enemy death floor sprite. should be
 * sprites. should be under enemies and under other floor sprites when
 * death happens on each character. should last 2 seconds after death of
 * enemy and be the exact size of the original sprite of the enemy that
 * died so there isnt a hugs sixe difference and is believebale. fade
 * the death sprite at the end of the 2 seconds."
 *
 * On enemy death, spawnDeathDecal() pushes a decal entry. The decal
 * renders BELOW everything else (under enemies, under loot, under
 * weapon ground effects, under item-effect floor sprites) at the
 * enemy's position with the SAME spriteSize as the alive enemy. Holds
 * full opacity for 1.5s, fades over the last 0.5s, removed at 2.0s.
 *
 * Only enemies with a registered DEATH_SPRITE_URLS entry produce a
 * decal — others (kevin, viewbotter, etc.) silently skip. Add new
 * entries to that table when sprites land.
 * =====================================================================
 */

const DEATH_DECAL_DURATION_SEC = 2.0;
const DEATH_DECAL_FADE_START_SEC = 1.5;

interface DeathDecal {
  x: number;
  y: number;
  spriteSize: number;
  definitionId: string;
  spawnedAt: number;       // game-time seconds (set from _currentTime at spawn)
}

const _decals: DeathDecal[] = [];

// Module-level "current time" cache. Engine pushes the latest game time
// each frame via tickDeathDecals so spawnDeathDecal can timestamp without
// every caller having to plumb time through the call chain.
let _currentTime = 0;

const DEATH_SPRITE_URLS: Record<string, string> = {
  zombie: '/enemies/deaths/zombie_death.png',
};

// BETA 16.24 — PSBOT cycle-25 P0.9. Per Dylan: "zombie death sprite is
// too small not matching actual size of the zombie i killed." Root cause:
// each death sprite is a square PNG with the figure surrounded by
// blood/slime/effect padding. The figure fills only ~40-70% of the
// frame depending on the asset. Drawing the decal at exactly enemy.
// spriteSize means the figure-portion is much smaller than the alive
// enemy sprite. Per-definitionId scale multiplier compensates so the
// FIGURE inside the decal reads as roughly the same size as the alive
// enemy. Default 1.6× for all (most padded), tuned per asset.
const DEATH_SPRITE_SCALE: Record<string, number> = {
  zombie: 1.8,    // green slime extends well past body — most padded
};

function getDeathSpriteScale(definitionId: string): number {
  return DEATH_SPRITE_SCALE[definitionId] ?? 1.6;
}

export function getDeathDecalUrls(): string[] {
  return Object.values(DEATH_SPRITE_URLS);
}

/**
 * Spawn a death decal at (x, y). Sized to match the enemy's spriteSize
 * (so the decal looks like the natural lying-down version of the same
 * enemy). Silently no-ops if the enemy's definitionId has no sprite
 * mapping. Caller fires this AFTER setting enemy.alive = false.
 */
export function spawnDeathDecal(
  definitionId: string,
  x: number,
  y: number,
  spriteSize: number,
): void {
  if (!DEATH_SPRITE_URLS[definitionId]) return;
  _decals.push({ x, y, spriteSize, definitionId, spawnedAt: _currentTime });
  // Cap at 80 decals to bound memory in case of mass-kill swarms.
  if (_decals.length > 80) _decals.splice(0, _decals.length - 80);
}

/**
 * Per-frame tick. Engine calls this once at the top of updateGame with
 * the current game time. Updates the module time cache + culls any
 * decals that have aged past the 2-second display window.
 */
export function tickDeathDecals(gameTime: number): void {
  _currentTime = gameTime;
  for (let i = _decals.length - 1; i >= 0; i--) {
    if (gameTime - _decals[i].spawnedAt >= DEATH_DECAL_DURATION_SEC) {
      _decals.splice(i, 1);
    }
  }
}

/**
 * Reset all decals. Engine calls on initGame so a new run starts clean.
 */
export function clearDeathDecals(): void {
  _decals.length = 0;
}

/**
 * Render all active decals at the BOTTOM of the world layer — below
 * weapon ground effects, below item-effect floor sprites, below enemies,
 * below loot, below the player. Engine inserts this call BEFORE
 * renderItemEffectsBelow so decals are the deepest visual layer.
 *
 * The provided getCachedImage helper is plumbed in by engine.ts to
 * avoid a circular dep (engine owns the image cache).
 */
export function renderDeathDecals(
  ctx: CanvasRenderingContext2D,
  camera: { x: number; y: number; zoom: number },
  width: number,
  height: number,
  gameTime: number,
  getCachedImage: (url: string) => HTMLImageElement | null,
): void {
  if (_decals.length === 0) return;
  for (const d of _decals) {
    const url = DEATH_SPRITE_URLS[d.definitionId];
    if (!url) continue;
    const img = getCachedImage(url);
    if (!img || !img.complete || img.naturalWidth === 0) continue;

    const elapsed = gameTime - d.spawnedAt;
    // Hold full opacity for the first 1.5s, then linearly fade over the
    // last 0.5s. At elapsed >= 2.0s the decal has been culled by tick().
    const alpha = elapsed < DEATH_DECAL_FADE_START_SEC
      ? 1.0
      : Math.max(
          0,
          (DEATH_DECAL_DURATION_SEC - elapsed) /
            (DEATH_DECAL_DURATION_SEC - DEATH_DECAL_FADE_START_SEC),
        );

    const screenX = (d.x - camera.x) * camera.zoom + width / 2;
    const screenY = (d.y - camera.y) * camera.zoom + height / 2;
    // BETA 16.24 — apply per-enemy decal scale so the figure inside the
    // padded death sprite reads as roughly the same visual size as the
    // alive enemy. See DEATH_SPRITE_SCALE table.
    const renderSize = d.spriteSize * camera.zoom * getDeathSpriteScale(d.definitionId);

    // Frustum cull
    if (
      screenX < -renderSize ||
      screenX > width + renderSize ||
      screenY < -renderSize ||
      screenY > height + renderSize
    ) continue;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
      img,
      screenX - renderSize / 2,
      screenY - renderSize / 2,
      renderSize,
      renderSize,
    );
    ctx.restore();
  }
}
